// frontend/src/pages/recruiter/PostJobForm.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./../../components/ui/button";
import { Input } from "./../../components/ui/input";
import { Label } from "./../../components/ui/label";
import { Textarea } from "./../../components/ui/textarea";
import { Alert, AlertDescription } from "./../../components/ui/alert";
import { 
  Loader2, 
  Shield, 
  AlertTriangle,
  Clock,
  CheckCircle,
  Mail,
  HelpCircle 
} from "lucide-react";
import api from "./../../api/client";
import { Switch } from "./../../components/ui/switch";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import Loader from "../../components/ui/Loader";
import { glassPanel } from "../../utils/glassStyles";

export default function PostJobForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categories, setCategories] = useState([]);
  
  // Approval Modal State
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [approvalStatus, setApprovalStatus] = useState({});
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await api.get("/jobs/categories");
        setCategories(res.data || []);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };
    fetchCats();
  }, []);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    requirements: "",
    category: "",
    location: "",
    salaryMin: "",
    salaryMax: "",
    deadline: "",
    tags: "",
    adminCanView: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post("/jobs/create-job", {
        ...formData,
        salaryMin: Number(formData.salaryMin),
        salaryMax: Number(formData.salaryMax),
        deadline: new Date(formData.deadline),
        tags: formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      
      setSuccess("Job posted for approval!");
      setTimeout(() => navigate("/recruiter/dashboard"), 2000);
    } catch (err) {
      console.error("Job creation error:", err);
      
      // Check if it's an approval error (403 Forbidden)
      if (err.response?.status === 403) {
        const errorData = err.response?.data;
        
        // Set modal content based on error response
        setModalTitle("Account Pending Approval");
        setModalMessage(errorData?.message || "Your recruiter account needs admin approval before posting jobs.");
        setApprovalStatus({
          status: errorData?.status || "PENDING",
          isApproved: errorData?.isApproved || 0
        });
        setShowApprovalModal(true);
      } else {
        // Handle other errors normally
        setError(err.response?.data?.message || "Failed to post job. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckApproval = async () => {
    setIsRetrying(true);
    try {
      // Try to check if recruiter is now approved
      const response = await api.get("/recruiter/profile");
      
      // If we get a successful response, the recruiter is approved
      if (response.data) {
        setShowApprovalModal(false);
        setSuccess("Your account is now approved! You can post jobs.");
        // Optionally refresh the page or reset form
      }
    } catch (err) {
      // Still not approved or other error
      console.error("Failed to check approval:", err);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <RecruiterPageShell
      title="Post a New Job"
      subtitle="Fill in the details below — your job will be reviewed before going live"
      onBack={() => navigate("/recruiter/dashboard")}
    >
      {success && (
        <Alert className="mb-6 border-green-200 dark:border-green-800 bg-green-500/10">
          <AlertDescription className="text-green-800 dark:text-green-300">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className={`rounded-2xl p-6 sm:p-8 ${glassPanel}`}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ... existing form fields remain the same ... */}
          <div>
            <Label htmlFor="title">Job Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Senior Frontend Engineer"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              placeholder="Describe the role..."
            />
          </div>

          <div>
            <Label htmlFor="requirements">Requirements (one per line)</Label>
            <Textarea
              id="requirements"
              rows={4}
              value={formData.requirements}
              onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {categories.slice(0, 6).map((cat) => (
                  <button
                    key={cat.title}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: cat.title })}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${
                      formData.category === cat.title
                        ? "bg-primary text-white border-primary shadow-md"
                        : "bg-primary/10 text-primary dark:text-primary/80 border-primary/20 dark:border-primary/30 hover:bg-primary/10 dark:hover:bg-primary/30"
                    }`}
                  >
                    {cat.title}
                  </button>
                ))}
              </div>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                placeholder="Software Engineering"
                className="focus:ring-2 focus:ring-primary"
              />
              <p className="text-[10px] text-muted-foreground">
                Select from popular categories or type your own.
              </p>
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                placeholder="Addis Ababa, Ethiopia"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="salaryMin">Min Salary (ETB)</Label>
              <Input
                id="salaryMin"
                type="number"
                value={formData.salaryMin}
                onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
                placeholder="50000"
              />
            </div>
            <div>
              <Label htmlFor="salaryMax">Max Salary (ETB)</Label>
              <Input
                id="salaryMax"
                type="number"
                value={formData.salaryMax}
                onChange={(e) => setFormData({ ...formData, salaryMax: e.target.value })}
                placeholder="80000"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="deadline">Application Deadline</Label>
            <Input
              id="deadline"
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="React, TypeScript, Remote"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl border border-primary/20 bg-primary/10/30 dark:border-primary/20 gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="admin-view" className="text-base font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Allow Admin View
              </Label>
              <p className="text-sm text-muted-foreground">
                Give access to the admin to see the application related info.
              </p>
            </div>
            <Switch
              id="admin-view"
              checked={formData.adminCanView}
              onCheckedChange={(checked) => setFormData({ ...formData, adminCanView: checked })}
            />
          </div>

          <Button 
            type="submit" 
            disabled={loading} 
            className="w-full h-14 text-base font-medium bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-[0.98]"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              "Post Job for Approval"
            )}
          </Button>
        </form>
      </div>

      {/* Approval Required Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full animate-in fade-in zoom-in duration-300">
            {/* Header */}
{/* Header */}
<div className="p-6 border-b border-gray-200 dark:border-gray-800">
  <div className="flex items-center gap-4">
    <div className={`p-3 rounded-xl ${
      approvalStatus.isApproved === 1 && approvalStatus.status === "APPROVED"
        ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
        : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
    }`}>
      {approvalStatus.isApproved === 1 && approvalStatus.status === "APPROVED" ? (
        <Shield className="w-6 h-6" />
      ) : (
        <Clock className="w-6 h-6" />
      )}
    </div>
    <div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
        {approvalStatus.isApproved === 1 && approvalStatus.status === "APPROVED"
          ? "Account Approved"
          : "Account Pending Approval"}
      </h3>
      
      {/* Status Details */}
      <div className="mt-2 space-y-1 text-sm">
        <p className="text-gray-500 dark:text-gray-400">
          Approval Status:{" "}
          <span className={`font-semibold ${
            approvalStatus.isApproved === 1 && approvalStatus.status === "APPROVED"
              ? "text-green-600" 
              : "text-amber-600"
          }`}>
            {approvalStatus.isApproved === 1 && approvalStatus.status === "APPROVED"
              ? "APPROVED"
              : "PENDING"}
          </span>
        </p>
        
        {/* Show detailed status */}
        <div className="flex items-center gap-4 text-xs">
          <span className={`px-2 py-1 rounded-full ${
            approvalStatus.isApproved === 1
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
          }`}>
            isApproved: {approvalStatus.isApproved === 1 ? "✓ Yes" : "✗ No"}
          </span>
          <span className={`px-2 py-1 rounded-full ${
            approvalStatus.status === "APPROVED"
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              : approvalStatus.status === "PENDING"
              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
          }`}>
            Status: {approvalStatus.status || "Not Set"}
          </span>
        </div>
      </div>
    </div>
  </div>
</div>
            {/* Body */}
            <div className="p-6 space-y-6">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {modalMessage}
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-1">
                      What happens next?
                    </h4>
                    <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Our admin team will review your company details
                      </li>
                      <li className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        You'll receive an email notification once approved
                      </li>
                      <li className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Once approved, you can post unlimited jobs
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-800 dark:text-gray-300 mb-1">
                      Need help?
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Contact our support team if you have questions about the approval process.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 py-3"
              >
                Close
              </Button>
              
              <Button
                onClick={handleCheckApproval}
                disabled={isRetrying}
                className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                {isRetrying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  "Check Approval Status"
                )}
              </Button>
              
              <Button
                onClick={() => {
                  setShowApprovalModal(false);
                  navigate("/recruiter/dashboard");
                }}
                className="flex-1 py-3 bg-gradient-to-r from-primary to-accent hover:brightness-110"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </RecruiterPageShell>
  );
}